import { Component, forwardRef, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../services/api.service';

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactComponent),
      multi: true
    }
  ]
})
export class ContactComponent implements ControlValueAccessor, OnInit {
  @Input() contactType: 'vendor' | 'customer' = 'vendor'; // Default to vendor for backward compatibility
  @Input() readonly: boolean = false;
  
  contactForm: FormGroup;
  private dataAlreadyProvided = false; // Track if data was provided by parent form
  
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService
  ) {
    this.contactForm = this.fb.group({
      id: [0],
      addressId: [0],
      firstName: [''],
      lastName: [''],
      email: [''],
      phone: ['']
    });

    // Subscribe to form value changes and propagate them to parent form
    this.contactForm.valueChanges.subscribe(value => {
      this.onChange(value);
      this.onTouched();
    });
  }

  ngOnInit(): void {
    // Detect contact type from URL if not explicitly set
    const currentUrl = this.router.url;
    if (currentUrl.includes('/customers')) {
      this.contactType = 'customer';
    } else if (currentUrl.includes('/vendors')) {
      this.contactType = 'vendor';
    }
    
    // Check if an ID route parameter is supplied
    const id = this.route.snapshot.paramMap.get('id');
    
    // Defer the API call to allow writeValue to run first
    // This prevents redundant API calls when data is already provided by parent
    if (id) {
      setTimeout(() => {
        if (!this.dataAlreadyProvided) {
          this.loadContact(id);
        }
      }, 0);
    }
  }

  loadContact(id: string): void {
    // Use the appropriate endpoint based on contact type
    const endpoint = `contact/${this.contactType}/${id}`;
    
    this.apiService.get<any>(endpoint).subscribe({
      next: (data) => {
        if (data) {
          const contact = {
            id: data.id ?? data.Id ?? 0,
            addressId: data.addressId || data.AddressId || 0,
            firstName: data.firstName || data.FirstName || '',
            lastName: data.lastName || data.LastName || '',
            email: data.email || data.Email || '',
            phone: data.phone || data.Phone || ''
          };
          this.contactForm.patchValue(contact);
        }
      },
      error: (error) => {
        console.error('Error loading contact:', error);
      }
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    // Always mark as provided — if writeValue is called at all, the parent controls this data
    this.dataAlreadyProvided = true;
    if (value) {
      // Handle both camelCase and PascalCase from API
      const contact = {
        id: value.id ?? value.Id ?? 0,
        addressId: value.addressId || value.AddressId || 0,
        firstName: value.firstName || value.FirstName || '',
        lastName: value.lastName || value.LastName || '',
        email: value.email || value.Email || '',
        phone: value.phone || value.Phone || ''
      };
      this.contactForm.patchValue(contact, { emitEvent: false });
    } else {
      this.contactForm.reset({ id: 0, addressId: 0 }, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.contactForm.disable();
    } else {
      this.contactForm.enable();
    }
  }
}
